class MarketplaceButtons extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.ctaSelector = this.dataset.ctaSelector ?? '';
    this.variantInputSelector = this.dataset.variantInputSelector ?? '';
    this.sectionRoot = document.getElementById(`shopify-section-${this.sectionId}`);

    try {
      this.variants = JSON.parse(this.dataset.variants ?? '{}');
    } catch (error) {
      console.error('marketplace-buttons: could not parse variant data', error);
      this.variants = {};
    }

    this.soldOutButton = this.querySelector('[data-marketplace-soldout-btn]');
    this.notice = this.querySelector('[data-marketplace-notice]');
    this.label = this.querySelector('[data-marketplace-label]');
    this.channelButtons = [...this.querySelectorAll('[data-marketplace-channel]')];

    this.currentVariantId = this.readVariantId();

    this.handleInputChange = this.handleInputChange.bind(this);
    this.handlePopState = this.handlePopState.bind(this);
    this.handleVariantEvent = this.handleVariantEvent.bind(this);
    this.handleSectionInteraction = this.handleSectionInteraction.bind(this);

    this.bindHooks();
    this.render();
  }

  disconnectedCallback() {
    this.unbindHooks();
  }

  /* ---------- hooks: keep currentVariantId in sync ---------- */

  bindHooks() {
    this.variantInput = this.queryVariantInput();

    if (this.variantInput) {
      // Stock theme fires `change` and/or mutates the value attribute.
      this.variantInput.addEventListener('change', this.handleInputChange);
      this.observer = new MutationObserver(this.handleInputChange);
      this.observer.observe(this.variantInput, {
        attributes: true,
        attributeFilter: ['value'],
      });
    }

    // Custom sections update the input via `.value =` (no event) — a delegated
    // listener on the section catches the option/sibling interaction and
    // re-reads on the next tick, once that handler has run.
    (this.sectionRoot ?? document).addEventListener('click', this.handleSectionInteraction);
    (this.sectionRoot ?? document).addEventListener('change', this.handleSectionInteraction);

    // ?variant= URL param (back/forward + programmatic pushState).
    window.addEventListener('popstate', this.handlePopState);
    this.wrapHistory();

    // Explicit variant-change custom events (names vary between themes).
    for (const name of MarketplaceButtons.VARIANT_EVENTS) {
      document.addEventListener(name, this.handleVariantEvent);
    }
    document.addEventListener(
      `eurus:product-page-variant-select:updated:${this.sectionId}`,
      this.handleVariantEvent
    );

    // Safety poll — last resort against paths that fire nothing observable.
    this.poll = setInterval(() => {
      const id = this.readVariantId();
      if (id && id !== this.currentVariantId) {
        this.currentVariantId = id;
        this.render();
      }
    }, 750);
  }

  unbindHooks() {
    this.variantInput?.removeEventListener('change', this.handleInputChange);
    this.observer?.disconnect();
    (this.sectionRoot ?? document).removeEventListener('click', this.handleSectionInteraction);
    (this.sectionRoot ?? document).removeEventListener('change', this.handleSectionInteraction);
    window.removeEventListener('popstate', this.handlePopState);
    for (const name of MarketplaceButtons.VARIANT_EVENTS) {
      document.removeEventListener(name, this.handleVariantEvent);
    }
    document.removeEventListener(
      `eurus:product-page-variant-select:updated:${this.sectionId}`,
      this.handleVariantEvent
    );
    clearInterval(this.poll);
  }

  wrapHistory() {
    if (window.history.pushState.__marketplaceWrapped) return;
    const original = window.history.pushState;
    const self = this;
    const wrapped = function wrappedPushState(...args) {
      const result = original.apply(this, args);
      self.handlePopState();
      return result;
    };
    wrapped.__marketplaceWrapped = true;
    window.history.pushState = wrapped;
  }

  /* ---------- event handlers ---------- */

  handleInputChange() {
    this.syncVariant(this.readVariantId());
  }

  handlePopState() {
    const params = new URLSearchParams(window.location.search);
    this.syncVariant(params.get('variant'));
  }

  handleVariantEvent(event) {
    const id =
      event?.detail?.variant?.id ??
      event?.detail?.currentVariantStatus?.id ??
      event?.detail?.id ??
      this.readVariantId();
    this.syncVariant(id);
  }

  handleSectionInteraction() {
    // Defer so the section's own click/change handler updates the input first.
    setTimeout(() => this.syncVariant(this.readVariantId()), 0);
  }

  syncVariant(id) {
    if (!id) return;
    const next = String(id);
    if (next === this.currentVariantId) return;
    this.currentVariantId = next;
    this.render();
  }

  /* ---------- state ---------- */

  queryVariantInput() {
    if (this.variantInputSelector) {
      const scoped = this.sectionRoot?.querySelector(this.variantInputSelector);
      if (scoped) return scoped;
      const global = document.querySelector(this.variantInputSelector);
      if (global) return global;
    }
    return (this.sectionRoot ?? document).querySelector(
      'form[action*="/cart/add"] input[name="id"]'
    );
  }

  readVariantId() {
    const fromInput = this.queryVariantInput()?.value;
    if (fromInput) return String(fromInput);

    const fromParam = new URLSearchParams(window.location.search).get('variant');
    return fromParam ? String(fromParam) : this.currentVariantId ?? null;
  }

  isSoldOut(data) {
    // Sold out ONLY when the metafield is explicitly true.
    // Unset / cleared / false => in-stock (normal flow), no availability fallback.
    return data?.isSoldOut === true;
  }

  render() {
    const data = this.variants[this.currentVariantId];
    const soldOut = this.isSoldOut(data);
    const hasLinks = Boolean(data?.channel1 || data?.channel2);

    this.classList.toggle('marketplace-buttons--sold-out', soldOut);
    // Sold out always shows the "Sold out" button (ATC replacement). The notice
    // + marketplace links only appear when this variant actually has link(s).
    if (this.soldOutButton) this.soldOutButton.hidden = !soldOut;
    if (this.notice) this.notice.hidden = !(soldOut && hasLinks);
    if (this.label) this.label.hidden = true;

    // Marketplace link buttons: only when sold out AND a URL is set for this variant.
    for (const button of this.channelButtons) {
      const key = button.dataset.marketplaceChannel === '1' ? 'channel1' : 'channel2';
      const url = data?.[key] ?? '';
      if (soldOut && url) {
        button.href = url;
        button.hidden = false;
      } else {
        button.removeAttribute('href');
        button.hidden = true;
      }
      button.classList.toggle('button-solid', soldOut);
      button.classList.toggle('button-outline', !soldOut);
    }

    this.toggleThemeCtas(soldOut);
  }

  toggleThemeCtas(hidden) {
    if (!this.ctaSelector) return;
    const scope = this.sectionRoot ?? document;
    for (const el of scope.querySelectorAll(this.ctaSelector)) {
      el.classList.toggle('marketplace-hidden-cta', hidden);
    }
  }
}

MarketplaceButtons.VARIANT_EVENTS = ['variant:change', 'variantChange', 'variant:changed'];

if (!customElements.get('marketplace-buttons')) {
  customElements.define('marketplace-buttons', MarketplaceButtons);
}
